<template>
  <v-card flat class="pa-0 record-form">
    <div v-if="readonly" class="clinical-readonly">
      <div class="clinical-readonly__grid">
        <div
          v-for="field in readonlyFields"
          :key="field.key"
          v-show="hasReadonlyValue(field.value)"
          class="clinical-readonly__item"
        >
          <p class="clinical-readonly__label">{{ field.label }}</p>
          <p class="clinical-readonly__value">{{ field.value }}</p>
        </div>
      </div>
      <p v-if="!readonlyFields.some(field => hasReadonlyValue(field.value))" class="caption grey--text ma-0">—</p>
    </div>
    <v-row v-else no-gutters>
      <v-col cols="12">
        <v-text-field
          v-model="localModel.vaccine"
          outlined
          dense
          :label="_getLabel('VACCINE_NAME')"
        ></v-text-field>
      </v-col>
      <v-col cols="12">
        <v-text-field
          v-model="localModel.manufacturer"
          outlined
          dense
          :label="_getLabel('MANUFACTURER')"
        ></v-text-field>
      </v-col>
      <v-col cols="12">
        <v-text-field
          v-model="localModel.batch"
          outlined
          dense
          :label="_getLabel('BATCH')"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6" class="pr-sm-2">
        <v-text-field
          v-model="manufacturingDateFormatted"
          outlined
          dense
          :label="_getLabel('MANUFACTURING_DATE')"
          type="date"
          :max="todayDate"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6" class="pl-sm-2">
        <v-text-field
          v-model="expirationDateFormatted"
          outlined
          dense
          :label="_getLabel('EXPIRATION_DATE')"
          type="date"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6" class="pr-sm-2">
        <v-text-field
          v-model="applicationDateFormatted"
          outlined
          dense
          :label="_getLabel('APPLICATION_DATE')"
          type="date"
          :max="todayDate"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6" class="pl-sm-2">
        <v-text-field
          v-model="nextDoseDateFormatted"
          outlined
          dense
          :label="_getLabel('NEXT_DOSE_DATE')"
          type="date"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6" class="pr-sm-2">
        <v-text-field
          v-model.number="localModel.dose"
          outlined
          dense
          type="number"
          :label="_getLabel('DOSE_NUMBER')"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6" class="pl-sm-2">
        <v-combobox
          v-model="localModel.protocol"
          :items="protocolSuggestions"
          outlined
          dense
          :label="_getLabel('PROTOCOL')"
        ></v-combobox>
      </v-col>
      <v-col cols="12">
        <v-textarea
          v-model="localModel.notes"
          outlined
          dense
          :label="_getLabel('NOTES')"
          rows="2"
        ></v-textarea>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import vaccinationsEnum from "@/constants/vaccinations.enum.js";
import {
  timestampToDateInput,
  dateInputToTimestamp,
  dateInputToClinicalTimestamp,
} from "@/constants/records.utils.js";

export default {
  name: "VaccinationForm",
  props: {
    value: {
      type: Object,
      default: () => ({}),
      required: false,
    },
    species: {
      type: Number,
      default: 0,
      required: false,
    },
    readonly: { type: Boolean, default: false },
  },
  data() {
    return {
      localModel: { ...this.value },
    };
  },
  computed: {
    todayDate() {
      return timestampToDateInput(Date.now());
    },
    readonlyFields() {
      return [
        ['vaccine', 'VACCINE_NAME', this.localModel.vaccine],
        ['manufacturer', 'MANUFACTURER', this.localModel.manufacturer],
        ['batch', 'BATCH', this.localModel.batch],
        ['manufacturingDate', 'MANUFACTURING_DATE', this.formatDate(this.localModel.manufacturingDate)],
        ['expirationDate', 'EXPIRATION_DATE', this.formatDate(this.localModel.expirationDate)],
        ['applicationDate', 'APPLICATION_DATE', this.formatDate(this.localModel.applicationDate)],
        ['nextDoseDate', 'NEXT_DOSE_DATE', this.formatDate(this.localModel.nextDoseDate)],
        ['dose', 'DOSE_NUMBER', this.localModel.dose],
        ['protocol', 'PROTOCOL', this.localModel.protocol],
        ['notes', 'NOTES', this.localModel.notes],
      ].map(([key, label, value]) => ({ key, label: this._getLabel(label), value }));
    },
    protocolSuggestions() {
      return vaccinationsEnum.getProtocolsForSpecies(this.species);
    },
    /**
     * Data clínica validada pelo backend: nunca pode ficar no futuro.
     */
    applicationDateFormatted: {
      get() {
        if (!this.localModel.applicationDate) return "";
        return timestampToDateInput(this.localModel.applicationDate);
      },
      set(val) {
        this.localModel.applicationDate = dateInputToClinicalTimestamp(val);
      },
    },
    nextDoseDateFormatted: {
      get() {
        if (!this.localModel.nextDoseDate) return "";
        return timestampToDateInput(this.localModel.nextDoseDate);
      },
      set(val) {
        this.localModel.nextDoseDate = dateInputToTimestamp(val) || undefined;
      },
    },
    manufacturingDateFormatted: {
      get() {
        if (!this.localModel.manufacturingDate) return "";
        return timestampToDateInput(this.localModel.manufacturingDate);
      },
      set(val) {
        this.localModel.manufacturingDate = dateInputToTimestamp(val) || undefined;
      },
    },
    expirationDateFormatted: {
      get() {
        if (!this.localModel.expirationDate) return "";
        return timestampToDateInput(this.localModel.expirationDate);
      },
      set(val) {
        this.localModel.expirationDate = dateInputToTimestamp(val) || undefined;
      },
    },
  },
  methods: {
    formatDate(value) {
      return Number.isFinite(value) ? this._getDateFromTimestamp(value) : "";
    },
    hasReadonlyValue(value) {
      return value !== null && value !== undefined && String(value).trim() !== "";
    },
  },
  watch: {
    value: {
      handler(val) {
        if (JSON.stringify(val) !== JSON.stringify(this.localModel)) {
          this.localModel = { ...(val || {}) };
        }
      },
      deep: true,
    },
    localModel: {
      handler(val) {
        if (JSON.stringify(val) !== JSON.stringify(this.value)) {
          this.$emit("input", val);
        }
      },
      deep: true,
    },
  },
};
</script>
<style scoped>
.record-form .v-input { margin-bottom: 8px; }
.clinical-readonly__grid { display: grid; gap: 8px; grid-template-columns: repeat(2, minmax(0, 1fr)); }
.clinical-readonly__item { background: #f5f6fa; border-radius: 8px; min-width: 0; padding: 10px 12px; }
.clinical-readonly__label { color: #667085; font-size: 11px; font-weight: 700; margin: 0 0 4px; }
.clinical-readonly__value { color: #193153; font-size: 14px; line-height: 1.45; margin: 0; overflow-wrap: anywhere; white-space: pre-wrap; }
@media (max-width: 599px) { .clinical-readonly__grid { grid-template-columns: 1fr; } }
</style>
